import { Suspense, lazy, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Spin } from 'antd';
import { kindOf } from '../documentKind';

// Loaded only when a PDF square actually becomes visible.
const PdfFirstPage = lazy(() => import('./PdfFirstPage'));

/**
 * useInView — true once the element has scrolled near the viewport, so a page
 * with many documents does not fetch and render every one up front. Without
 * IntersectionObserver (old browsers, server render, tests) content mounts
 * immediately — the safe default is "show it".
 */
function useInView() {
  const ref = useRef(null);
  const [inView, setInView] = useState(() => typeof IntersectionObserver === 'undefined');
  useEffect(() => {
    if (inView) return undefined;
    const el = ref.current;
    if (!el) return undefined;
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        setInView(true);
        io.disconnect();
      }
    }, { rootMargin: '200px' });
    io.observe(el);
    return () => io.disconnect();
  }, [inView]);
  return [ref, inView];
}

function IconCard({ doc, renderIcon }) {
  return (
    <div className="v1-doc-preview__card">
      {renderIcon ? renderIcon(doc.name) : null}
      <span className="v1-doc-preview__card-name">{doc.name}</span>
    </div>
  );
}

function PreviewSquare({ doc, index, onOpen, renderIcon }) {
  const [ref, inView] = useInView();
  const [imageFailed, setImageFailed] = useState(false);
  const kind = kindOf(doc);
  const open = () => onOpen(index);

  let content = null;
  if (inView) {
    if (kind === 'image' && !imageFailed) {
      content = (
        <img
          src={doc.url}
          alt={doc.name}
          loading="lazy"
          className="v1-doc-preview__img"
          onError={() => setImageFailed(true)}
        />
      );
    } else if (kind === 'pdf') {
      content = (
        <Suspense fallback={<Spin size="small" />}>
          <PdfFirstPage url={doc.url} name={doc.name} />
        </Suspense>
      );
    } else {
      content = <IconCard doc={doc} renderIcon={renderIcon} />;
    }
  }

  return (
    // A div with a button role, not a <button>: a PDF can fall back to an
    // iframe, and interactive content is not allowed inside a real button.
    <div
      ref={ref}
      role="button"
      tabIndex={0}
      className="v1-doc-preview"
      title={`Preview ${doc.name}`}
      aria-label={`Open preview of ${doc.name}`}
      onClick={open}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          open();
        }
      }}
    >
      {content}
      <span className="v1-doc-preview__caption">{doc.name}</span>
    </div>
  );
}

/**
 * DocumentPreviewGrid — every document shown as an inline 1:1 square below the
 * field, the file shrunk to fit inside; clicking a square opens the full viewer
 * at that document.
 *
 * `docs` must be the SAME previewable list the viewer receives, so the square's
 * index is the viewer's index. Opt-in via renderOptions.previewOpenByDefault.
 */
export default function DocumentPreviewGrid({ docs, onOpen, renderIcon }) {
  if (!Array.isArray(docs) || docs.length === 0) return null;
  return (
    <div className="v1-doc-preview-grid">
      {docs.map((doc, i) => (
        <PreviewSquare
          key={doc.key ?? `${doc.url}-${i}`}
          doc={doc}
          index={i}
          onOpen={onOpen}
          renderIcon={renderIcon}
        />
      ))}
    </div>
  );
}

DocumentPreviewGrid.propTypes = {
  docs: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string,
    url: PropTypes.string,
    type: PropTypes.string,
  })),
  onOpen: PropTypes.func.isRequired,
  renderIcon: PropTypes.func,
};

PreviewSquare.propTypes = {
  doc: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  onOpen: PropTypes.func.isRequired,
  renderIcon: PropTypes.func,
};

IconCard.propTypes = {
  doc: PropTypes.object.isRequired,
  renderIcon: PropTypes.func,
};
